import { compose, memoize } from 'lodash/fp';

import { delay, preventPromiseRejection } from '@skippay/core/utils/promiseUtils';

import { loadScript as loadScriptAction, scriptLoadError, scriptLoaded } from './slice';

const INIT_TIMEOUT = 7000;

const externalScriptsMiddleware =
	(config) =>
	({ dispatch, getState }) => {
		// memoization prevents load script more than once
		const loadScript = memoize(async (name) => {
			const scriptConfig = config[name];

			try {
				if (!scriptConfig) {
					throw new Error(`Unsupported scriptName: ${name}`);
				}

				const { country } = getState().user;
				const { load, init, initTimeout = INIT_TIMEOUT } = scriptConfig;
				let result = await load({ country });
				if (init) {
					result = await Promise.race([
						init(),
						delay(initTimeout).then(() =>
							Promise.reject(
								new Error(
									`${name} script loaded but failed to initialize within ${initTimeout} milliseconds.`
								)
							)
						),
					]);
				}
				dispatch(scriptLoaded(name));
				return result;
			} catch (error) {
				dispatch(scriptLoadError({ error: String(error), scriptName: name }));
				throw error;
			}
		});

		return (next) => (action) => {
			if (loadScriptAction.match(action)) {
				const scripts = action.payload;

				if (!scripts) {
					return Promise.resolve();
				}

				return Array.isArray(scripts)
					? // dispatch always fulfils and will contain result for each script
						Promise.all(
							scripts.map(compose(preventPromiseRejection, (script) => loadScript(script)))
						)
					: // dispatch either resolves or rejects
						loadScript(scripts);
			}

			return next(action);
		};
	};

export default externalScriptsMiddleware;
